import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  Brand,
  FindVehicleBrandsEntity,
  FindVehicleModelsEntity,
  FindVehicleTypeError,
  Model,
  OperationType,
} from "./interfaces";

import { VEHICLE_TYPE } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/vehicleConstants";
import { isDefined } from "Utils/functions";

type VehicleBrandMap = {
  [year: string]: FindVehicleBrandsEntity;
};

type VehicleModelMap = {
  [year: string]: {
    [brand: string]: FindVehicleModelsEntity;
  };
};

export interface FindVehicleTypeState {
  brands: VehicleBrandMap;
  models: VehicleModelMap;
}

const initialState: FindVehicleTypeState = {
  brands: {},
  models: {},
};

const findVehicleTypeSlice = createSlice({
  name: "findVehicleType",
  initialState,
  reducers: {
    findBrandsByYearStart: (
      state,
      {
        payload: { year },
      }: PayloadAction<{ vehicleType: VEHICLE_TYPE; year: string }>,
    ) => {
      if (!isDefined(state.brands[year])) {
        state.brands[year] = {
          current: null,
          isLoading: true,
          error: null,
        };
      } else {
        state.brands[year].isLoading = true;
      }
    },
    findBrandsByYearSucceeded: (
      state,
      {
        payload: { brands, year },
      }: PayloadAction<{ brands: Brand[]; year: string }>,
    ) => {
      state.brands[year].current = brands;
      state.brands[year].isLoading = false;
      state.brands[year].error = null;
    },

    findModelsByYearAndBrandStart: (
      state,
      {
        payload: { year, brand },
      }: PayloadAction<{
        vehicleType: VEHICLE_TYPE;
        year: string;
        brand: string;
      }>,
    ) => {
      if (!isDefined(state.models[year])) {
        state.models[year] = {};
      }
      if (!isDefined(state.models[year][brand])) {
        state.models[year][brand] = {
          current: null,
          isLoading: true,
          error: null,
        };
      } else {
        state.models[year][brand].isLoading = true;
      }
    },
    findModelsByYearAndBrandSucceeded: (
      state,
      {
        payload: { year, brand, models },
      }: PayloadAction<{ year: string; brand: string; models: Model[] }>,
    ) => {
      state.models[year][brand].current = models;
      state.models[year][brand].isLoading = false;
      state.models[year][brand].error = null;
    },

    operationFailed: (
      state,
      {
        payload: { year, brand, error },
      }: PayloadAction<{
        year: string;
        brand?: string;
        error: FindVehicleTypeError;
      }>,
    ) => {
      switch (error.operationType) {
        case OperationType.FIND_BRANDS_BY_YEAR:
          state.brands[year].isLoading = false;
          state.brands[year].error = error;
          break;
        case OperationType.FIND_MODELS_BY_YEAR_AND_BRAND:
          if (brand) {
            state.models[year][brand].isLoading = false;
            state.models[year][brand].error = error;
          }
          break;
      }
    },
  },
});

export const {
  findBrandsByYearStart,
  findBrandsByYearSucceeded,
  findModelsByYearAndBrandStart,
  findModelsByYearAndBrandSucceeded,
  operationFailed,
} = findVehicleTypeSlice.actions;
export const findVehicleType = findVehicleTypeSlice.reducer;
